import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiHeart,
  FiActivity,
  FiDroplet,
  FiThermometer,
  FiTrendingUp,
  FiTrendingDown,
  FiFileText,
  FiCalendar,
} from "react-icons/fi";
import OurLayouts from "../layout";
import { User } from "lucide-react";
import { Tabs, Modal } from "antd";
import { APP_ROUTES } from "../../constants/approutes";

const { TabPane } = Tabs;

const tabStyle = {
  vitals: { color: "#00897b", fontWeight: 700 },
  medical: { color: "#F97316", fontWeight: 700 },
  prescriptions: { color: "#6B21A8", fontWeight: 700 },
  labReports: { color: "#2563EB", fontWeight: 700 },
  appointments: { color: "#D97706", fontWeight: 700 },
};

const API_BASE = process.env.REACT_APP_API_URL;

const vitalsColors = {
  temperature: { bg: "bg-green-50", text: "text-green-600" },
  respirationRate: { bg: "bg-indigo-50", text: "text-indigo-600" },
  bloodPressure: { bg: "bg-cyan-50", text: "text-cyan-700" },
  oxygenLevel: { bg: "bg-red-50", text: "text-red-600" },
  heartRate: { bg: "bg-pink-100", text: "text-pink-700" },
  height: { bg: "bg-teal-50", text: "text-teal-600" },
  weight: { bg: "bg-red-50", text: "text-red-400" },
  bloodSugar: { bg: "bg-yellow-50", text: "text-yellow-700" },
};

function PatientInfoCard({ patient }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:gap-8">
      <div className="bg-[#0076794f] rounded-full p-4 shadow-lg">
        <User className="w-24 h-24 text-white" />
      </div>
      <div className="flex-1 text-center md:text-left">
        <h2 className="text-3xl font-semibold text-[#004276] mb-1">
          {patient.firstName} {patient.lastName}
        </h2>
        <p className="text-gray-600 mb-5">
          {patient.age} years | {patient.gender}
        </p>
        <div className="text-sm space-y-2 text-gray-700">
          <p>
            <span className="font-semibold">Address:</span> {patient.address},{" "}
            {patient.pinCode}
          </p>
          <p>
            <span className="font-semibold">Mobile:</span>{" "}
            {patient.mobileNumber}
          </p>
        </div>
      </div>
    </div>
  );
}

function VitalCard({ label, value, icon, colorClass, onViewAll }) {
  return (
    <div className={`${colorClass} rounded-lg p-4 w-full shadow-md`}>
      <div className="text-xl mb-2">{icon}</div>
      <div className="font-semibold text-sm">{label}</div>
      <div className="text-lg font-bold">{value}</div>
      <div className="mt-2 text-right">
        <button
          className="text-[#004276] hover:underline text-sm font-semibold"
          onClick={onViewAll}
        >
          View All
        </button>
      </div>
    </div>
  );
}

const getEmbeddableUrl = (url) => url;

export default function PatientDetailsdata() {
  const { state } = useLocation();
  const patientCode = state?.patientCode;
  const [patient, setPatient] = useState(null);
  const [vitals, setVitals] = useState([]);
  const [history, setHistory] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [docModalVisible, setDocModalVisible] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [digitalprescription, setDigitalprescription] = useState([]);
  const navigate = useNavigate();

  const handleViewAll = (type) => {
    navigate(APP_ROUTES.VIEWVITAL, {
      state: {
        type,
        patientCode,
      },
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    const config = { headers: { Authorization: `Bearer ${token}`, tenantId: tenantId } };

    async function fetchInitialData() {
      try {
        const [pRes, vRes, hRes, aRes, dpRes] = await Promise.all([
          axios.get(
            `${API_BASE}patient/search?patientId=${patientCode}`,
            config
          ),
          axios.get(
            `${API_BASE}patient-vitals/by-patient?patientCode=${patientCode}`,
            config
          ),
          axios.get(
            `${API_BASE}patient-medical-history/by-patient?patientCode=${patientCode}`,
            config
          ),
          axios.get(
            `${API_BASE}queue/appointmentlist?patientId=${patientCode}`,
            config
          ),
          axios.get(
            `${API_BASE}prescriptions/docs?patientId=${patientCode}`,
            config
          ),
        ]);

        setPatient(pRes?.data.data[0]);
        setVitals(vRes.data.data || []);
        console.log(dpRes.data, "dxcfgvhbjkn");
        setDigitalprescription(dpRes.data);

        // Sort history descending by recordTime so newest is first
        const sortedHistory = (hRes.data.data || []).sort(
          (a, b) => new Date(b.recordTime) - new Date(a.recordTime)
        );
        setHistory(sortedHistory);

        setAppointments(aRes.data || []);
      } catch (e) {
        console.error(e);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    if (patientCode) {
      fetchInitialData();
    } else {
      setError("No patient code provided");
      setLoading(false);
    }
  }, [patientCode]);

  // Fetch a signed URL for any document (medical, prescription, or lab)
  const fetchAndShowDocument = async (fileName) => {
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      const response = await fetch(
        `${API_BASE}patient-medical-history/file-url?fileName=${fileName}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
        }
      );
      const result = await response.json();
      if (result.signedUrl) {
        setDocUrl(result.signedUrl);
        // Check file extension
        setIsPdf(fileName.toLowerCase().endsWith(".pdf"));
        setDocModalVisible(true);
      } else {
        alert("Failed to retrieve document URL");
      }
    } catch (err) {
      alert("Failed to fetch document URL");
    }
  };

  if (loading) {
    return (
      <OurLayouts>
        <div className="p-8 text-center">Loading...</div>
      </OurLayouts>
    );
  }

  if (error) {
    return (
      <OurLayouts>
        <div className="p-8 text-center text-red-600">{error}</div>
      </OurLayouts>
    );
  }

  const totalRecords = history.length;

  const latestEntry = history[0] || {};
  const latestEntryWithDoc = history.find((h) => h.documentUrl) || {};
  const latestEntryWithLab =
    history.find(
      (h) => Array.isArray(h.labReportUrls) && h.labReportUrls.length > 0
    ) || {};
  console.log(digitalprescription, "fcgvhbjnkml");
  return (
    <>
      <div className=" max-w-6xl mx-auto bg-[#00b77e0a] min-h-screen">
        {/* Patient Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <PatientInfoCard patient={patient} />
        </div>

        <Tabs defaultActiveKey="1">
          {/* Vitals Tab */}
          <TabPane tab={<span style={tabStyle.vitals}>Vitals</span>} key="1">
            {vitals.length === 0 && <p>No vitals records available.</p>}

            {vitals.length > 0 && (
              <div className="bg-white rounded-lg p-4 shadow mb-4">
                <h4 className="text-lg font-semibold mb-4 text-[#004276]">
                  Latest Vitals Record
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (
                    {new Date(
                      vitals[vitals.length - 1].createdDate
                    ).toLocaleString()}
                    )
                  </span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <VitalCard
                    label="Temperature"
                    value={`${vitals[vitals.length - 1].temperature} °F`}
                    icon={<FiThermometer />}
                    colorClass={`${vitalsColors.temperature.bg} ${vitalsColors.temperature.text}`}
                    onViewAll={() => handleViewAll("temperature")}
                  />
                  <VitalCard
                    label="Respiration Rate"
                    value={`${vitals[vitals.length - 1].respirationRate} /min`}
                    icon={<FiActivity />}
                    colorClass={`${vitalsColors.respirationRate.bg} ${vitalsColors.respirationRate.text}`}
                    onViewAll={() => handleViewAll("respirationRate")}
                  />
                  <VitalCard
                    label="Blood Pressure"
                    value={`${
                      vitals[vitals.length - 1].bloodPressureSystolic
                    }/${vitals[vitals.length - 1].bloodPressureDiastolic} mmHg`}
                    icon={<FiDroplet />}
                    colorClass={`${vitalsColors.bloodPressure.bg} ${vitalsColors.bloodPressure.text}`}
                    onViewAll={() => handleViewAll("bloodPressure")}
                  />
                  <VitalCard
                    label="Oxygen Level"
                    value={`${vitals[vitals.length - 1].oxygenLevel} %`}
                    icon={<FiHeart />}
                    colorClass={`${vitalsColors.oxygenLevel.bg} ${vitalsColors.oxygenLevel.text}`}
                    onViewAll={() => handleViewAll("oxygenLevel")}
                  />
                  <VitalCard
                    label="Heart Rate"
                    value={`${vitals[vitals.length - 1].heartRate} bpm`}
                    icon={<FiHeart />}
                    colorClass={`${vitalsColors.heartRate.bg} ${vitalsColors.heartRate.text}`}
                    onViewAll={() => handleViewAll("heartRate")}
                  />
                  <VitalCard
                    label="Height"
                    value={
                      vitals[vitals.length - 1].heightFeet > 0
                        ? `${vitals[vitals.length - 1].heightFeet} ft ${
                            vitals[vitals.length - 1].heightInches
                          } in`
                        : `${vitals[vitals.length - 1].heightCm} cm`
                    }
                    icon={<FiTrendingUp />}
                    colorClass={`${vitalsColors.height.bg} ${vitalsColors.height.text}`}
                    onViewAll={() => handleViewAll("height")}
                  />
                  <VitalCard
                    label="Weight"
                    value={`${vitals[vitals.length - 1].weightInKg} kg`}
                    icon={<FiTrendingDown />}
                    colorClass={`${vitalsColors.weight.bg} ${vitalsColors.weight.text}`}
                    onViewAll={() => handleViewAll("weight")}
                  />
                  <VitalCard
                    label="Blood Glucose"
                    value={`${vitals[vitals.length - 1].bloodGlucose} mg/dL`}
                    icon={<FiDroplet />}
                    colorClass={`${vitalsColors.bloodSugar.bg} ${vitalsColors.bloodSugar.text}`}
                    onViewAll={() => handleViewAll("bloodGlucose")}
                  />
                </div>
              </div>
            )}
          </TabPane>

          {/* Medical History Tab */}
          <TabPane
            tab={<span style={tabStyle.medical}>Medical Reports</span>}
            key="2"
          >
            {history.length === 0 && <p>No medical history available.</p>}

            {history.length > 0 && (
              <div className="space-y-6">
                {/* Latest Entry */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="font-semibold text-gray-700 mb-2">
                    Date: ({new Date(latestEntry.recordTime).toLocaleString()})
                  </h4>
                  <ul className="text-gray-700 text-sm space-y-2">
                    <li>
                      <strong>Allergies:</strong> {latestEntry.allergyHistory}
                    </li>
                    <li>
                      <strong>Medical History:</strong>{" "}
                      {latestEntry.medicalHistory}
                    </li>
                    <li>
                      <strong>Surgical History:</strong>{" "}
                      {latestEntry.surgicalHistory}
                    </li>
                    <li>
                      <strong>Family History:</strong>{" "}
                      {latestEntry.familyHistory}
                    </li>
                    <li>
                      <strong>Social History:</strong>{" "}
                      {latestEntry.socialHistory}
                    </li>
                    <li>
                      <strong>Medication:</strong> {latestEntry.medication}
                    </li>
                  </ul>
                </div>

                {/* Older Entries */}
                {history.length > 1 && (
                  <details className="bg-white rounded-lg p-4 shadow">
                    <summary className="cursor-pointer text-blue-600 font-medium mb-3">
                      Show older medical history
                    </summary>
                    <div className="space-y-6 mt-3">
                      {history.slice(1).map((entry, idx) => {
                        const recordNum = totalRecords - (idx + 1);
                        return (
                          <div
                            key={entry.id || idx}
                            className="bg-white rounded-lg p-4 shadow"
                          >
                            <h4 className="font-semibold text-gray-700 mb-2">
                              Date: ({" "}
                              {new Date(entry.recordTime).toLocaleString()})
                            </h4>
                            <ul className="text-gray-700 text-sm space-y-2">
                              <li>
                                <strong>Allergies:</strong>{" "}
                                {entry.allergyHistory}
                              </li>
                              <li>
                                <strong>Medical History:</strong>{" "}
                                {entry.medicalHistory}
                              </li>
                              <li>
                                <strong>Surgical History:</strong>{" "}
                                {entry.surgicalHistory}
                              </li>
                              <li>
                                <strong>Family History:</strong>{" "}
                                {entry.familyHistory}
                              </li>
                              <li>
                                <strong>Social History:</strong>{" "}
                                {entry.socialHistory}
                              </li>
                              <li>
                                <strong>Medication:</strong> {entry.medication}
                              </li>
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
              </div>
            )}
          </TabPane>

          {/* Prescriptions Tab */}
          <TabPane
            tab={<span style={tabStyle.prescriptions}>Prescriptions</span>}
            key="3"
          >
            {/* <div className="bg-white rounded-lg shadow p-6 text-gray-600">
                <p className="mb-2 font-semibold">Prescription Document:</p>
                <button
                  onClick={() =>
                    fetchAndShowDocument(digitalprescription)
                  }
                  className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                >
                  <FiFileText className="inline-block" /> View Prescription
                </button>
              </div> */}
            {/* {Array.isArray(digitalprescription) && digitalprescription.length > 0 && (
   <>
      {digitalprescription.map((fileName, idx) => (
        <button
          key={fileName + idx}
          onClick={() => fetchAndShowDocument(fileName)}
          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
        >
          <FiFileText className="inline-block" />
          {`View Prescription #${idx + 1}`}
        </button>
      ))}
    </>
  )} */}
            <div className="bg-white rounded-lg shadow p-6 text-gray-600">
              <p className="mb-2 font-semibold">Prescription Document:</p>
              {!latestEntryWithDoc.documentUrl ? (
                <p>No prescription document available.</p>
              ) : (
                <button
                  onClick={() =>
                    fetchAndShowDocument(latestEntryWithDoc.documentUrl)
                  }
                  className="text-blue-600 hover:underline text-sm flex items-center gap-1 mb-2"
                >
                  <FiFileText className="inline-block" /> View Prescription
                </button>
              )}
              {Array.isArray(digitalprescription) &&
                digitalprescription.length > 0 && (
                  <>
                    {digitalprescription.map((fileName, idx) => (
                      <button
                        key={fileName + idx}
                        onClick={() => fetchAndShowDocument(fileName)}
                        className="text-blue-600 hover:underline text-sm flex items-center gap-1 mb-2"
                      >
                        <FiFileText className="inline-block" />
                        {`View Prescription`}
                      </button>
                    ))}
                  </>
                )}
            </div>
          </TabPane>

          {/* Lab Reports Tab */}
          <TabPane
            tab={<span style={tabStyle.labReports}>Lab Reports</span>}
            key="4"
          >
            {!latestEntryWithLab.labReportUrls ||
            latestEntryWithLab.labReportUrls.length === 0 ? (
              <p>No lab report documents available.</p>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-gray-600 space-y-4">
                <p className="font-semibold">Lab Report Documents:</p>
                {latestEntryWithLab.labReportUrls.map((fileName, idx) => (
                  <button
                    key={idx}
                    onClick={() => fetchAndShowDocument(fileName)}
                    className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                  >
                    <FiFileText className="inline-block" /> View Lab Report #
                    {idx + 1}
                  </button>
                ))}
              </div>
            )}
          </TabPane>

          {/* Appointment Details Tab */}
          <TabPane
            tab={<span style={tabStyle.appointments}>Appointment Details</span>}
            key="5"
          >
            {appointments.length === 0 ? (
              <p>No appointments on record.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {appointments.map((appt) => (
                  <div
                    key={appt.id}
                    className="bg-white p-4 rounded-lg shadow flex flex-col md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#e1f5fe] p-3 rounded-full">
                        <FiCalendar className="text-2xl text-[#004276]" />
                      </div>
                      <div>
                        <p className="text-lg font-semibold">
                          Token: {appt.tokenNumber}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(appt.whenAppointment).toLocaleDateString()}{" "}
                          • {appt.fromTime} - {appt.toTime}
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Patient:</span>{" "}
                          {appt.patientName || "N/A"} (
                          {appt.patientMobileNo || "N/A"})
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Reason:</span>{" "}
                          {appt.consultationsReason}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0 flex flex-col md:items-end space-y-1">
                      <p>
                        <span className="font-semibold">Status:</span>{" "}
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${
                            appt.status === "COMPLETED"
                              ? "bg-green-100 text-green-700"
                              : appt.status === "CANCELLED"
                              ? "bg-red-100 text-red-700"
                              : appt.status === "SKIPPED"
                              ? "bg-yellow-100 text-yellow-700"
                              : appt.status === "ONGOING"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-pink-100 text-pink-700"
                          }`}
                        >
                          {appt.status}
                        </span>
                      </p>
                      <p>
                        <span className="font-semibold">Payment:</span>{" "}
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${
                            appt.paymentStatus === "SUCCESS"
                              ? "bg-green-100 text-green-700"
                              : appt.paymentStatus === "ERROR"
                              ? "bg-red-100 text-red-700"
                              : appt.paymentStatus === "INITIATED"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-pink-100 text-pink-700"
                          }`}
                        >
                          {appt.paymentStatus}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabPane>
        </Tabs>

        {/* Modal for viewing any document */}
        <Modal
          open={docModalVisible}
          onCancel={() => setDocModalVisible(false)}
          width={800}
          footer={
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-[#004276] text-white rounded"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = docUrl;
                  link.download = docUrl.split("/").pop();
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download
              </button>
              <button
                onClick={() => setDocModalVisible(false)}
                className="px-4 py-2 border border-gray-400 text-gray-600 rounded hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          }
        >
          {isPdf ? (
            <iframe
              src={docUrl}
              title="PDF Preview"
              className="w-full h-[80vh]"
              frameBorder="0"
            />
          ) : (
            <img
              src={docUrl}
              alt="Document Preview"
              className="w-full max-h-[80vh] object-contain"
            />
          )}
        </Modal>
      </div>
    </>
  );
}
